/**
 * My Book View - Main Component
 * Top-level advisor dashboard showing aggregated data across all clients
 * Matches ClientsPage design pattern for consistency
 */

import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import SecondaryNav from '../layout/SecondaryNav';
import { BookOpen, TrendingUp, Users, AlertCircle } from 'lucide-react';
import PortfolioFilters, { ClientOption, PortfolioOption } from './filters/PortfolioFilters';
import TopHoldingsTable from './TopHoldingsTable';
import MyBookClientList from './MyBookClientList';
import HoldingDrillDown from './HoldingDrillDown';
import BookPerformanceWidget from './BookPerformanceWidget';
import PortfolioAIChat from './PortfolioAIChat';
import { useMyBookData, TopHolding } from '../../hooks/useMyBookData';
import {
  usePortfolioFilters,
  useNavigationActions,
  useFilterActions,
} from '../../services/portfolio-refactor/store/portfolioStore';
import { AccountFilter } from '../../types/portfolio-views';

const MyBookView: React.FC = () => {
  const navigate = useNavigate();

  // TASK-207: Use PortfolioFilters instead of MyBookFilters
  const portfolioFilters = usePortfolioFilters();
  const { drillDown } = useNavigationActions();
  const { setPortfolioFilters } = useFilterActions();

  // Benchmark toggle state
  const [benchmark, setBenchmark] = useState<'SPI' | 'SMI'>('SPI');

  // AI Chat: highlight & scroll state
  const [highlightedSection, setHighlightedSection] = useState<string>('');
  const [scrollTarget, setScrollTarget] = useState<string>('');

  // Refs for scroll-to-section
  const performanceRef = useRef<HTMLDivElement>(null);
  const summaryRef = useRef<HTMLDivElement>(null);
  const holdingsRef = useRef<HTMLDivElement>(null);
  const clientsRef = useRef<HTMLDivElement>(null);

  // Fetch data with PortfolioFilters and benchmark
  const { data, loading, error } = useMyBookData(portfolioFilters, benchmark);

  // Modal state for holding drill-down
  const [selectedHolding, setSelectedHolding] = useState<TopHolding | null>(null);
  const [isDrillDownOpen, setIsDrillDownOpen] = useState(false);

  // Collapsible client list state
  const [showClients, setShowClients] = useState(false);

  // Handle holding click (open drill-down)
  const handleHoldingClick = (holding: TopHolding) => {
    setSelectedHolding(holding);
    setIsDrillDownOpen(true);
  };

  // Handle client click (navigate to client portfolio view)
  const handleClientClick = (clientId: string) => {
    drillDown('client', clientId);
    navigate(`/portfolio/client/${clientId}`); // TASK-206: Navigate to Client Level View
  };

  // AI Chat handlers
  const handleAITabChange = (target: string) => {
    setScrollTarget(target);
  };

  const handleAIFilterChange = (filter: any) => {
    setPortfolioFilters(filter);
  };

  const handleAIHighlight = (section: string) => {
    setHighlightedSection(section);
    setTimeout(() => setHighlightedSection(''), 3000);
  };

  const handleAIScroll = (target: string) => {
    setScrollTarget(target);
  };

  // Scroll to section when scrollTarget changes
  useEffect(() => {
    if (!scrollTarget) return;

    const refMap: Record<string, React.RefObject<HTMLDivElement>> = {
      performance: performanceRef,
      summary: summaryRef,
      holdings: holdingsRef,
      clients: clientsRef,
    };

    const ref = refMap[scrollTarget];
    if (ref?.current) {
      ref.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    setTimeout(() => setScrollTarget(''), 1000);
  }, [scrollTarget]);

  // TASK-277: Use only real data from API (no mock data)
  const clientOptions: ClientOption[] = useMemo(() => {
    if (!data?.clients) return [];

    return data.clients.map(client => ({
      id: client.customerId,
      name: client.customerName,
      totalAuM: client.totalAuM
    }));
  }, [data]);

  const portfolioOptions: PortfolioOption[] = useMemo(() => {
    if (!data?.clients) return [];

    const portfolios: PortfolioOption[] = [];
    data.clients.forEach(client => {
      // Each client may have portfolios
      const clientPortfolios = (client as any).portfolios || [];
      clientPortfolios.forEach((portfolio: any) => {
        const portfolioId = portfolio.id || portfolio.portfolio_id;
        const portfolioName = portfolio.name || portfolio.portfolio_name;
        const portfolioValue = portfolio.value || portfolio.total_value;

        if (portfolioId && portfolioName) {
          portfolios.push({
            id: portfolioId,
            name: portfolioName,
            clientId: client.customerId,
            value: portfolioValue
          });
        }
      });
    });
    return portfolios;
  }, [data]);

  // Mock available accounts
  const availableAccounts: AccountFilter[] = useMemo(() => {
    // Empty for My Book level (accounts are per-portfolio)
    return [];
  }, []);

  // Format currency
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('de-CH', {
      style: 'currency',
      currency: 'CHF',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <SecondaryNav />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          {/* Header */}
          <div className="flex justify-between items-center mb-6">
            <div>
              <h2 className="text-2xl font-bold text-[#002B45]">My Book - Portfolio Analysis</h2>
              <p className="text-sm text-gray-600 mt-1">
                Cross-client portfolio analytics and concentration risk analysis
              </p>
            </div>
            {!loading && data && (
              <div className="text-right">
                <p className="text-sm text-gray-500">
                  {data.clients.length} clients • {data.summary.totalPortfolios} portfolios
                </p>
                <p className="text-lg font-semibold text-gray-900">
                  {formatCurrency(data.summary.totalAuM)}
                </p>
              </div>
            )}
          </div>

          {/* TASK-207: Filters - Now using PortfolioFilters for consistency */}
          <PortfolioFilters
            filters={portfolioFilters}
            availableAccounts={availableAccounts}
            onFiltersChange={(partialFilters) => setPortfolioFilters(partialFilters)}
            clients={clientOptions}
            portfolios={portfolioOptions}
            historicalSupported={false} // TODO: Enable when backend supports historical snapshots
            showAccountsFilter={false}
            showAssetClassFilter={true}
            showCurrencyFilter={true}
            showDateFilter={false}
          />

          {/* Loading State */}
          {loading && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-kielis-navy mx-auto"></div>
                <p className="mt-4 text-gray-600">Analyzing portfolio data across clients...</p>
              </div>
            </div>
          )}

          {/* Error State */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-red-800 mb-2">
                Error Loading Portfolio Analysis
              </h3>
              <p className="text-red-600">{typeof error === 'string' ? error : (error as any).message}</p>
            </div>
          )}

          {/* Portfolio Analytics Content */}
          {!loading && !error && data && (
            <>
              {/* Book Performance Widget */}
              <div ref={performanceRef} className={`mb-6 ${highlightedSection === 'performance' ? 'ring-4 ring-blue-400 rounded-lg transition-all' : ''}`}>
                <div className="flex items-center justify-end mb-2 gap-1">
                  <span className="text-xs text-gray-500 mr-1">Benchmark:</span>
                  {(['SPI', 'SMI'] as const).map((code) => (
                    <button
                      key={code}
                      onClick={() => setBenchmark(code)}
                      className={`px-3 py-1 text-xs font-medium rounded-full transition-colors ${
                        benchmark === code
                          ? 'bg-[#002B45] text-white'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      {code === 'SPI' ? 'SPI 20' : 'SMI'}
                    </button>
                  ))}
                </div>
                <BookPerformanceWidget
                  bookPerformanceYTD={data.summary.bookPerformanceYTD}
                  bookPerformanceMTD={data.summary.bookPerformanceMTD}
                  benchmarkYTD={data.summary.benchmarkYTD}
                  alpha={data.summary.alpha}
                  benchmarkName={data.summary.benchmarkName}
                />
              </div>

              {/* Portfolio Summary Cards */}
              <div ref={summaryRef} className={`grid grid-cols-1 md:grid-cols-4 gap-6 mb-6 ${highlightedSection === 'summary' ? 'ring-4 ring-blue-400 rounded-lg transition-all' : ''}`}>
                <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <TrendingUp className="w-8 h-8 text-[#F7B500]" />
                    </div>
                    <div className="ml-4">
                      <p className="text-2xl font-semibold text-[#002B45]">{formatCurrency(data.summary.totalAuM)}</p>
                      <p className="text-sm text-gray-600">Total AuM</p>
                    </div>
                  </div>
                </div>

                <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <BookOpen className="w-8 h-8 text-[#002B45]" />
                    </div>
                    <div className="ml-4">
                      <p className="text-2xl font-semibold text-[#002B45]">{data.topHoldings.length}</p>
                      <p className="text-sm text-gray-600">Unique Holdings</p>
                    </div>
                  </div>
                </div>

                <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <AlertCircle className={`w-8 h-8 ${
                        data.topHoldings.filter(h => h.percentOfBook > 10).length > 0
                          ? 'text-[#FFC107]'
                          : 'text-[#28A745]'
                      }`} />
                    </div>
                    <div className="ml-4">
                      <p className="text-2xl font-semibold text-[#002B45]">
                        {data.topHoldings.filter(h => h.percentOfBook > 10).length}
                      </p>
                      <p className="text-sm text-gray-600">Concentrated ({'>'}10%)</p>
                    </div>
                  </div>
                </div>

                <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <Users className="w-8 h-8 text-[#002B45]" />
                    </div>
                    <div className="ml-4">
                      <p className="text-2xl font-semibold text-[#002B45]">
                        {formatCurrency(data.summary.avgClientAuM)}
                      </p>
                      <p className="text-sm text-gray-600">Avg Client AuM</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Top Holdings - PRIMARY FOCUS */}
              <div ref={holdingsRef} className={highlightedSection === 'holdings' ? 'ring-4 ring-blue-400 rounded-lg transition-all' : ''}>
              <TopHoldingsTable
                holdings={data.topHoldings}
                onHoldingClick={handleHoldingClick}
                loading={false}
              />
              </div>

              {/* Client List - Secondary (collapsible) */}
              <div ref={clientsRef} className={`mt-6 ${highlightedSection === 'clients' ? 'ring-4 ring-blue-400 rounded-lg transition-all' : ''}`}>
                <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                  <button
                    onClick={() => setShowClients(!showClients)}
                    className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <Users className="w-5 h-5 text-gray-600" />
                      <h3 className="text-lg font-semibold text-gray-900">
                        Client List ({data.clients.length})
                      </h3>
                    </div>
                    <span className="text-sm text-gray-500">
                      {showClients ? 'Hide' : 'Show'} clients
                    </span>
                  </button>

                  {showClients && (
                    <div className="border-t p-6">
                      <MyBookClientList
                        clients={data.clients}
                        onClientClick={handleClientClick}
                      />
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </main>

      {/* Holding Drill-Down Modal */}
      <HoldingDrillDown
        holding={selectedHolding}
        isOpen={isDrillDownOpen}
        onClose={() => setIsDrillDownOpen(false)}
        onClientClick={handleClientClick}
      />

      {/* AI Portfolio Assistant */}
      {!loading && !error && data && (
        <PortfolioAIChat
          clientData={data}
          onTabChange={handleAITabChange}
          onFilterChange={handleAIFilterChange}
          onHighlight={handleAIHighlight}
          onScroll={handleAIScroll}
        />
      )}
    </div>
  );
};

export default MyBookView;
